import React, { useEffect, useState } from 'react';
import { ButtonField, Dropdown, DropdownList, DropdownListItem, Iconsvg, InputField, Modal, ModalHeader, ModalBody, ModalFooter, ButtonGroup, ButtonGroupItem, Tooltip, TooltipBody } from '@wk/components-react16';
import DOMPurify from "dompurify";
import { Toast } from '../../services';
import { isValidJSON } from '../../utils/isValisJSON';
import { MarkdownWithTargetBlank } from './MarkdownWithTargetBlank';
import { AnyARecord } from 'node:dns';
import { useDispatch, useSelector } from 'react-redux';
import { hideLoading, showLoading } from '../../store/authReducer';
import { RootState } from '../../store';
import { API_URL } from '../../config/constants/apiUrl';
import HttpService from "../../config/http.service";

const ResponseTab = ({ data }: any) => {
	const { runPromptResponse, setRunPromptResponse, validationResponse, promptType, reqAuthoringValidationResponse, promptId, setPromptValidationList } = data;
    const placement: any = {
        offsets: {
            left: {
                leftOffset: 8,
            },
            'top-left': {
                leftOffset: 8,
            },
            right: {
                leftOffset: -8,
            },
            'top-right': {
                leftOffset: -8,
            }
        },
    };
    const dispatch = useDispatch();
    const [activeTab, setActiveTab] = useState('response');
    const { authProject } = useSelector((state: RootState) => state.auth);
    const [openValidationId, setOpenValidationId] = useState<string | null>(null);
    const [reqHighlights, setReqHighlights] = useState<Record<string, string>>({});
    const [editValidation, setEditValidation] = useState<any>(null);
    const aiSuggestedValidationCases =
        (Array.isArray(validationResponse?.ai_suggested_validation_cases) && validationResponse.ai_suggested_validation_cases.length > 0
            ? validationResponse.ai_suggested_validation_cases
            : reqAuthoringValidationResponse?.[0]?.validationResponse?.ai_suggested_validation_cases) || [];

    const handleCopy = async (index: number, node: any) => {
        try {
            runPromptResponse[index].isCopy = true;
            const text = activeTab === 'response' ? node.response : node.rationale;
            await navigator.clipboard.writeText(text);
            setRunPromptResponse([...runPromptResponse]);
            setTimeout(() => {
                runPromptResponse[index].isCopy = false;
                setRunPromptResponse([...runPromptResponse]);
            }, 500);
        } catch (err) {
            Toast('Failed to copy text: ' + activeTab, "error");
        }
    };

    const highlightRefText = (item: any, responseIndex: number = 0) => {
        // Remove all existing span tags with highlighted-text class
        let cleanResponse = runPromptResponse[responseIndex].response.replace(/<span class="highlighted-text-red">([^<]*)<\/span>/g, '$1');
        cleanResponse = cleanResponse.replace(/<span class="highlighted-text-green">([^<]*)<\/span>/g, '$1');

            // Add new highlight to all occurrences using replaceAll
            if(item.reference.length > 0) {
                item.reference.forEach((refText: string) => {
                if(item.status){
                    cleanResponse = cleanResponse.replaceAll(refText, `<span class="highlighted-text-green">${refText}</span>`);
                } else {
                    cleanResponse = cleanResponse.replaceAll(refText, `<span class="highlighted-text-red">${refText}</span>`);
                }
            });
            }
            runPromptResponse[responseIndex].response = cleanResponse;
            setRunPromptResponse([...runPromptResponse]);
    }
    const highlightReqRefText = (validationItem: any, requirementContent: string, responseIndex: number, requirementIndex: number) => {
        const key = `${responseIndex}-${requirementIndex}`;
        let highlighted = requirementContent;
        if (validationItem.reference.length > 0) {
            validationItem.reference.forEach((refText: string) => {
                if (validationItem.status) {
                    highlighted = highlighted.replaceAll(refText, `<span class="highlighted-text-green">${refText}</span>`);
                } else {
                    highlighted = highlighted.replaceAll(refText, `<span class="highlighted-text-red">${refText}</span>`);
                }
            });
        }
        setReqHighlights(prev => ({ ...prev, [key]: highlighted }));
    }

    const toggleValidationList = (id: string) => {
        setOpenValidationId(prev => prev === id ? null : id);
    }

    const parseResponsePayload = (response: any) => {
        if (!response) {
            return null;
        }

        if (typeof response === 'object') {
            return response;
        }

        if (typeof response === 'string' && isValidJSON(response)) {
            return JSON.parse(response);
        }

        return null;
    }

    const renderDefaultResponse = (item: any) => {
        if (item.is_json_output && typeof item.response === 'string' && isValidJSON(item.response)) {
            return <code className='language-json'>{JSON.stringify(JSON.parse(item.response), null, 2)}</code>;
        }

        return (
            <MarkdownWithTargetBlank>
                {typeof item.response === 'string' ? item.response : JSON.stringify(item.response, null, 2)}
            </MarkdownWithTargetBlank>
        );
    }

    const renderRequirementAuthoringResponse = (item: any, responseIndex: number) => {
        const parsedResponse = parseResponsePayload(item.response);
        const requirements = Array.isArray(parsedResponse?.requirements) ? parsedResponse.requirements : [];

        if (!requirements.length) {
            return renderDefaultResponse(item);
        }

        return (
            <div>
                {requirements.map((requirement: any, requirementIndex: number) => {
                    const requirementLabel = `Requirement ${requirementIndex + 1}`;
                    const requirementContent = typeof requirement === 'string' ? requirement : JSON.stringify(requirement, null, 2);
                    let reqvalidationResponse:any = {};
                    const validationDropdownId = `validation-dropdown-${requirementIndex}`;
                    if(reqAuthoringValidationResponse?.length > 0) {
                        reqvalidationResponse = reqAuthoringValidationResponse.find((resp: any) => resp.requirementTitle === requirement.title)?.validationResponse;
                    }
                    return (
                        <div className="mb-15 p-10 border rounded-2" key={`${requirement?.id || 'req'}-${requirementIndex}`}>
                            <div className="flex space-between">
                                <div className="wk-weight-500 mb-5">{requirementLabel}</div>
                                {reqvalidationResponse && Object.keys(reqvalidationResponse).length > 0 && (
                                    <div>
                                        {Object.keys(reqvalidationResponse).length > 0 &&
                                            <span onClick={() => toggleValidationList(validationDropdownId)} id={validationDropdownId} className="badge rounded-pill border mr-10 text-dark cursor-pointer" style={{ backgroundColor: '#f8d8a6' }}>
                                                <span className='wk-weight-500'>Validation</span> : {reqvalidationResponse.passed_testcases} / {reqvalidationResponse.total_testcases}
                                                </span>
                                        }
                                        <div className="validation-dropdown">
                                            <Dropdown
                                                targetSelector={`#${validationDropdownId}`}
                                                menuMode="application"
                                                srLabelledby={validationDropdownId}
                                                controlMode="controlled"
                                                isOpen={openValidationId === validationDropdownId}
                                                onUserRequest={() => toggleValidationList(validationDropdownId)}
                                                >
                                                <DropdownList menuMode="application" >
                                                    {Object.keys(reqvalidationResponse).length > 0 &&
                                                        <>
                                                            {Array.isArray(reqvalidationResponse.llm_response) && reqvalidationResponse.llm_response.length > 0 ? reqvalidationResponse.llm_response.map((item: any, index: any) =>
                                                                    <DropdownListItem slot="dropdownListItem" onItemClick={() => {toggleValidationList(validationDropdownId); highlightReqRefText(item, requirementContent, responseIndex, requirementIndex)}} key={index}>
                                                                        <div className={`text-${item.status ? 'success bg-success' : 'danger bg-danger'} text-dark rounded bg-opacity-10 p-5`}>
                                                                            {item.rule}
                                                                        </div>
                                                                    </DropdownListItem>
                                                            ) : null}
                                                        </>
                                                    }
                                                </DropdownList>
                                            </Dropdown>
                                        </div>
                                    </div>
                                    
                                )}

                            </div>
                            <pre className="mb-0">
                                {reqHighlights[`${responseIndex}-${requirementIndex}`]
                                    ? <code className="language-json" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(reqHighlights[`${responseIndex}-${requirementIndex}`], { ALLOWED_TAGS: ['span'], ALLOWED_ATTR: ['class'] }) }} />
                                    : <code className="language-json">{requirementContent}</code>
                                }
                            </pre>
                        </div>
                    );
                })}
            </div>
        );
    }


    const addValidation = (validation:any) => {
            dispatch(showLoading());
            const payload: any = {
                projectId: authProject.id,
                id: null,
                name: validation.name,
                text: validation.text,
                prompts: [promptId]
            }
    
            HttpService.postRequest(API_URL.ADD_VALIDATION, payload).then(
                (res: any) => {
                    dispatch(hideLoading());
                    Toast('Validation rule added successfully.', "success");
                    setPromptValidationList((prevList: any) => [...prevList, res]);
                },
                (err: any) => {
                    dispatch(hideLoading());
                    Toast(err.message, "error");
                }
            )
    }



    return (
        <div className='custom-tooltip' data-component="ResponseTab">
            <ul className="nav nav-pills mb-0" id="pills-tab" role="tablist">
                <li className="nav-item" role="presentation">
                    <button
                        className={`nav-link position-relative ${activeTab === 'response' ? 'active' : ''}`}
                        id="pills-response-tab"
                        data-bs-toggle="pill"
                        data-bs-target="#pills-response"
                        type="button"
                        role="tab"
                        aria-controls="pills-response"
                        aria-selected={activeTab === 'response' ? 'true' : 'false'}
                        onClick={() => setActiveTab('response')}
                    >
                        Response
                    </button>
                </li>
                <li className="nav-item" role="presentation">
                    <button
                        className={`nav-link position-relative ${activeTab === 'rationale' ? 'active' : ''}`}
                        id="pills-rationale-tab"
                        data-bs-toggle="pill"
                        data-bs-target="#pills-rationale"
                        type="button"
                        role="tab"
                        aria-controls="pills-rationale"
                        aria-selected={activeTab === 'rationale' ? 'true' : 'false'}
                        onClick={() => setActiveTab('rationale')}
                    >
                        Rationale
                    </button>
                </li>
                <li className="nav-item" role="presentation">
                    <button
                        className={`nav-link position-relative ${activeTab === 'aiValidations' ? 'active' : ''}`}
                        id="pills-ai-validations-tab"
                        data-bs-toggle="pill"
                        data-bs-target="#pills-ai-validations"
                        type="button"
                        role="tab"
                        aria-controls="pills-ai-validations"
                        aria-selected={activeTab === 'aiValidations' ? 'true' : 'false'}
                        onClick={() => setActiveTab('aiValidations')}
                    >
                        AI Suggested Validations
                    </button>
                </li>
                <li className="nav-item ms-auto pt-10" role="presentation">
                    {Object.keys(validationResponse).length > 0 && <span onClick={() => toggleValidationList('validation-dropdown')} id="validation-dropdown" className="badge rounded-pill border mr-10 text-dark cursor-pointer" style={{ backgroundColor: '#f8d8a6' }}><span className='wk-weight-500'>Validation</span> : {validationResponse.passed_testcases} / {validationResponse.total_testcases}</span>
                    }
                    <div className="validation-dropdown">
                        <Dropdown
                            targetSelector="#validation-dropdown"
                            menuMode="application"
                            srLabelledby="validation-dropdown"
                            controlMode="controlled"
                            isOpen={openValidationId === 'validation-dropdown'}
                            onUserRequest={() => toggleValidationList('validation-dropdown')}
                            >
                            <DropdownList menuMode="application" >
                                {Object.keys(validationResponse).length > 0 &&
                                    <>
                                        {Array.isArray(validationResponse.llm_response) && validationResponse.llm_response.length > 0 ? validationResponse.llm_response.map((item: any, index: any) =>
                                                <DropdownListItem slot="dropdownListItem" onItemClick={() => {toggleValidationList('validation-dropdown'); highlightRefText(item)}} key={index}>
                                                    <div className={`text-${item.status ? 'success bg-success' : 'danger bg-danger'} text-dark rounded bg-opacity-10 p-5`}>
                                                        {item.rule}
                                                    </div>
                                                </DropdownListItem>
                                        ) : null}
                                    </>
                                }
                            </DropdownList>
                        </Dropdown>
                    </div>
                </li>
            </ul>
            <div className="tab-content border rounded-3 border-primary bg-white" id="pills-tabContent">
                <div className={`tab-pane fade ${activeTab === 'response' ? 'show active' : ''}`} id="pills-response" role="tabpanel" aria-labelledby="pills-response-tab">
                    <div className="accordion" id="accordionResponse">
                        {runPromptResponse.length > 0 ? runPromptResponse.map((item: any, index: any) => (
                            <div key={index} className="accordion-item">
                                <h2 className="accordion-header position-relative">
                                    <button className={`accordion-button ${index != 0 ? 'collapsed' : ''}`} type="button"
                                        data-bs-toggle="collapse" data-bs-target={`#collapseResponse${index}`}
                                        aria-expanded={index == 0 ? true : false}
                                        aria-controls={`collapseResponse${index}`}>
                                        {item.documentName ? item.documentName : `Response`}
                                    </button>
                                    <span className="position-absolute cursor-pointer" style={{ zIndex: 4, top: '4px', right: '60px'}} onClick={() => handleCopy(index, item)}>
                                        <Iconsvg name={item?.isCopy ? "check" : "files"} color='#9A67F3'></Iconsvg>
                                    </span>
                                </h2>
                                <div id={`collapseResponse${index}`}
                                    className={`accordion-collapse collapse ${index == 0 ? 'show' : ''}`} data-bs-parent="#accordionResponse">
                                    <div className="accordion-body 222">
                                        <div>
                                            {promptType === 'Requirement Authoring'
                                                ? renderRequirementAuthoringResponse(item, index)
                                                : renderDefaultResponse(item)}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )) : null}
                    </div>
                </div>
                <div className={`tab-pane fade ${activeTab === 'rationale' ? 'show active' : ''}`} id="pills-rationale" role="tabpanel" aria-labelledby="pills-rationale-tab">
                    <div className="accordion" id="accordionRationale">
                        {runPromptResponse.length > 0 ? runPromptResponse.map((item: any, index: any) => (
                            <div key={index} className="accordion-item">
                                <h2 className="accordion-header position-relative">
                                    <button className={`accordion-button ${index != 0 ? 'collapsed' : ''}`} type="button"
                                        data-bs-toggle="collapse" data-bs-target={`#collapseRationale${index}`}
                                        aria-expanded={index == 0 ? true : false}
                                        aria-controls={`collapseRationale${index}`}>
                                        {item.documentName ? item.documentName : `Rationale`}
                                    </button>
                                    <span className="position-absolute cursor-pointer" style={{ zIndex: 4, top: '4px', right: '60px' }} onClick={(event: any) => handleCopy(index, item)}>
                                        <Iconsvg name={item?.isCopy ? "check" : "files"} color='#9A67F3'></Iconsvg>
                                    </span>
                                </h2>
                                <div id={`collapseRationale${index}`} className={`accordion-collapse collapse ${index == 0 ? 'show' : ''}`} data-bs-parent="#accordionRationale">
                                    <div className="accordion-body 111">
                                        <pre>
                                            <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.rationale, { ALLOWED_TAGS: ["highlight"], }) }}></div>
                                        </pre>
                                    </div>
                                </div>
                            </div>
                        )) : null}
                    </div>
                </div>
                <div className={`tab-pane fade ${activeTab === 'aiValidations' ? 'show active' : ''}`} id="pills-ai-validations" role="tabpanel" aria-labelledby="pills-ai-validations-tab">
                    <ul className="list-unstyled mb-0 p-3">
                        {Array.isArray(aiSuggestedValidationCases) && aiSuggestedValidationCases.length > 0
                            ? aiSuggestedValidationCases.map((validation: any, index: number) => (
                            <li key={`${validation?.name || 'validation'}-${index}`} className="d-flex align-items-center justify-content-between gap-3 p-3 mb-2 border rounded">
                                <div>
                                    <p>{validation?.name}</p>
                                    <p style={{fontSize: '12px'}}><strong>Description:</strong> {validation?.text}</p>
                                </div>
                                <div className="d-flex gap-2">
                                    <ButtonField size={'small'} mode={'ghost'}>
                                        <button title="Reset" name="reset" type="button" onClick={() => addValidation(validation)}>
                                            Add to prompt
                                        </button>
                                    </ButtonField>
                                    <ButtonField size={'small'} mode={'ghost'}>
                                        <button title="Edit" name="edit" type="button" onClick={() => setEditValidation({ ...validation })}>
                                            Edit
                                        </button>
                                    </ButtonField>
                                </div>
                            </li>
                            ))
                            : <li className="text-muted">No AI suggested validations available.</li>}
                    </ul>
                </div>
            </div>
            <div className='custom-modal'>
                <Modal
                    isOpen={!!editValidation}
                    srLabelledBy={'modal-id-edit-validation'}
                    srDescribedBy={'modal-id-edit-validation'}>
                    <ModalHeader
                        titleId={'modal-id-edit-validation'}
                        onUserRequest={() => setEditValidation(null)}>
                        Edit Validation
                    </ModalHeader>
                    <ModalBody descriptionId="modal-id-edit-validation">
                        <InputField label="Validation Name" labelFor="edit-validation-name" size="small" indicator="required">
                            <input
                                type="text" id="edit-validation-name" name="name"
                                placeholder="Validation Name" autoComplete="off"
                                value={editValidation?.name || ''}
                                onChange={(event: any) => setEditValidation((prev: any) => ({ ...prev, name: event.target.value }))}
                            />
                        </InputField>
                        <InputField label="Validation Description" labelFor="edit-validation-text" size="small" indicator="required">
                            <textarea
                                id="edit-validation-text" name="text"
                                placeholder="Validation Description" autoComplete="off"
                                value={editValidation?.text || ''}
                                onChange={(event: any) => setEditValidation((prev: any) => ({ ...prev, text: event.target.value }))}
                            />
                        </InputField>
                    </ModalBody>
                    <ModalFooter>
                        <ButtonGroup mode="static">
                            <ButtonGroupItem slot="buttonGroupItem">
                                <ButtonField mode={'text'}>
                                    <button type="button" onClick={() => setEditValidation(null)}>Cancel</button>
                                </ButtonField>
                                <ButtonField>
                                    <button
                                        type="button"
                                        disabled={!editValidation?.name?.trim()}
                                        onClick={() => { addValidation(editValidation); setEditValidation(null); }}>
                                        Add to prompt
                                    </button>
                                </ButtonField>
                            </ButtonGroupItem>
                        </ButtonGroup>
                    </ModalFooter>
                </Modal>
            </div>
            {/* {Object.keys(validationResponse).length > 0 && <Tooltip
                identifier="tooltip-bottom-left"
                mode="interactive"
                position="bottom-left"
                targetSelector="#button-top-right"
                placementOptions={placement}
                size='auto'
                >
                <TooltipBody slot="tooltipBody">
                    {validationResponse.llm_response.length > 0 ? validationResponse.llm_response.map((item: any, index: any) =>
                        <div onClick={() => highlightRefText(item)} className={`mt-10 p-5 pt-2 pb-2 text-dark d-flex rounded bg-opacity-10 ${item.status ? 'bg-success' : 'bg-danger cursor'}`} key={index}>
                            <div className='mr-10'>{item.rule}</div>
                            <div className={`ms-auto text-${item.status ? 'success' : 'danger'}`}><Iconsvg size={12} name={item.status ? "check-circle" : "stop-sign"}></Iconsvg></div>
                        </div>
                    ) : null}
                </TooltipBody>
            </Tooltip>} */}
        </div>
    )
}

export default ResponseTab